/**
 * Created by Ning Jiang on 2021-06-18.
 */

import {gsap, Power4, TimelineLite} from "gsap";
import {MathHelper} from "../../../../core/utils/MathHelper";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/component/BitmapCountUp";
import {DDayGameAssets} from "../../../DDayGameAssets";
import {DDayLayers, Layers} from "../../../view/DDayLayers";
import * as PIXI from "pixi.js";
import {Impact} from "../../../Font/Impact";
import {BLEND_MODES} from "pixi.js";
import {DDay} from "../../../DDay";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {Translation} from "../../../../core/utils/Translation";
import {BlitzKriegSound} from "../../../sound/BlitzKriegSound";

export class InitialWinCountUp extends PIXI.Container {

    protected countUpContainer: PIXI.Container;
    protected _countUp: BitmapCountUp;
    protected _waysWonText: PIXI.Text;
    private _endingScale: number = 0.8;
    private _effect: TimelineSprite;

    constructor() {
        super();

        const style = {
            fontName: DDayGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 30,
        };
        const waysWonStyle = new PIXI.TextStyle({
            fontFamily: Impact.FAMILY,
            fontSize: "50px",
            stroke: "#000000",
            strokeThickness: 10,
            fill: "#EFEBDE",
            dropShadowColor: "#000000",
            dropShadowAlpha: 0.95,
            dropShadowAngle: 90,
            dropShadowDistance: 17,
            lineJoin: "round"
        });
        this._countUp = new BitmapCountUp(style);
        this._countUp.shrinkSize = true;
        this._countUp.visible = false;
        this._waysWonText = new PIXI.Text("", waysWonStyle);
        this._waysWonText.anchor = new PIXI.Point(0.5, 0.5);
        this._waysWonText.visible = false;
        this._waysWonText.position = new PIXI.Point(-4, 100);

        this._effect = new TimelineSprite(DDay.assetLoader.getTextures("reel_smoke/reel_smoke"));
        this._effect.blendMode = BLEND_MODES.SCREEN;
        this._effect.anchor.set(0.5, 0.5);
        this._effect.scale.set(2, 2);
        this._effect.position.set(0, -20);
        this._effect.alpha = 0;
        this.countUpContainer = new PIXI.Container();
        this.countUpContainer.addChild(this._countUp, this._waysWonText);
        this.addChild(this._effect, this.countUpContainer);
        this.position.set(360, 360);
        DDayLayers.getLayer(Layers.WIN_COUNT_UP)?.addChild(this);
    }

    private getScale(multiplier: number): number {
        return 1 + (multiplier - 1) * 0.1;
    }

    public updateWinCount(winNumber: number, waysCount: number): void {
        this._countUp.text = winNumber.toFixed(2);
        this._countUp.maxTextWidth = this._countUp.maxTextWidth = 600 / this.getScale(1);
        this._waysWonText.text = waysCount.toString() + " " + (waysCount === 1 ? Translation.translation("Way Won") : Translation.translation("Ways Won"));
        this._countUp.alpha = 1;
        this._countUp.visible = true;
        this._waysWonText.alpha = 1;
        this._waysWonText.visible = true;
    }

    public getCountUpAnimation(from: number, to: number, duration: number, multiplier: number, waysCount: number = 1): TimelineLite {
        const baseToNumber = multiplier === 1 ? to : MathHelper.roundToDecimals(to / multiplier, 2);
        const tl = gsap.timeline();
        tl.add([
            () => {
                this._countUp.maxTextWidth = this._countUp.maxTextWidth = 600 / this.getScale(multiplier);
                this._countUp.scale.set(1);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._waysWonText.text = waysCount.toString() + " " + (waysCount === 1 ? Translation.translation("Way Won") : Translation.translation("Ways Won"));
                this._waysWonText.scale.set(1);
                this._waysWonText.visible = true;
                this._waysWonText.alpha = 1;
                this.countUpContainer.y = -this.countUpContainer.height / 2;
                if (duration > 0.01) {
                    // play count up sound.
                }
            },
            this._countUp.getCountUpAnimation(from, baseToNumber, duration)
        ]);

        if (multiplier > 1) {
            const levelTime: number = 0.5 * multiplier > 5 ? MathHelper.roundToDecimals(5 / multiplier, 2) : 0.5;
            for (let m: number = 1; m <= multiplier; m++) {
                const showNumber: number = baseToNumber * m;
                const scale: number = this.getScale(m);
                tl.add([
                    () => {
                        // ElPasoGunfightSoundConfig.soundPlayer.playTrack({name : ElPasoGunfightSoundConfig.instance.COUNT_UP_MULTIPLIER});
                    },
                    this._countUp.getCountUpAnimation(showNumber, showNumber, 0.01),
                    gsap.to([this._countUp.scale, this._waysWonText.scale],
                        {
                            duration: levelTime / 2,
                            x: scale,
                            y: scale,
                            ease: `elastic.out(1.1, 1)`
                        }),
                    gsap.to(this, {duration: levelTime}) // a pause for half levelTime
                ]);
            }
        }
        tl.add(() => {
            if (DDay.model.parsedGameData.currentWinRatioConfig?.name === "Tiny win") {
                BlitzKriegSound.playTrack({name: BlitzKriegSound.TINY_WIN})
            } else if (DDay.model.parsedGameData.currentWinRatioConfig?.name === "Small win") {
                BlitzKriegSound.playTrack({name: BlitzKriegSound.SMALL_WIN})
            } else if (DDay.model.parsedGameData.currentWinRatioConfig?.name === "Medium win") {
                BlitzKriegSound.playTrack({name: BlitzKriegSound.MEDIUM_WIN})
            } else if (DDay.model.parsedGameData.currentWinRatioConfig?.name === "Large win") {
                BlitzKriegSound.playTrack({name: BlitzKriegSound.LARGE_WIN})
            }
            if (DDay.model.parsedGameData.currentWinRatioConfig?.index === 2 || DDay.model.parsedGameData.currentWinRatioConfig?.index === 3) {
                BlitzKriegSound.fadeAndStopSound({
                    name: BlitzKriegSound.WIN_COUNT_UP,
                    from: 1,
                    to: 0,
                    duration: 0.02
                });
            }
        })

        // ending stay
        tl.add(() => {
            this.stay(multiplier);
            this.playBackgroundEffect();
        });
        tl.to(this, {
            duration: 1
        });
        return tl;
    }

    private stay(multiplier: number): TimelineLite {
        const tl = gsap.timeline({
            onStart: () => {
                BlitzKriegSound.playTrack({name: BlitzKriegSound.WIN_FALL_BACK});
            }
        });
        tl.add([
            gsap.to([this._countUp.scale, this._waysWonText.scale], {
                duration: 0.2,
                x: this._endingScale,
                y: this._endingScale,
                ease: Power4.easeOut
            }), () => {
                this._waysWonText.alpha = 1;
                this._waysWonText.visible = true;
            }
        ]);
        // timeline.add(() => ElPasoGunfightSoundConfig.soundPlayer.playTrack({name : ElPasoGunfightSoundConfig.instance.COUNT_UP_END_FALL_BACK}), "-=0.3");
        return tl;
    }

    public remove(): TimelineLite {
        const tl = gsap.timeline();
        if (this._countUp.visible) {
            tl.add(gsap.to(this._countUp, {
                duration: 0.5,
                alpha: 0,
                y: -100,
                ease: `power2.in`
            }), 0);
            tl.add(gsap.to(this._waysWonText, {
                duration: 0.5,
                alpha: 0,
                y: 0,
                ease: `power2.in`
            }), 0);// stay a short while after count up.
            tl.add(() => {
                this._countUp.y = 0;
                this._countUp.visible = false;
                this._waysWonText.y = 100;
                this._waysWonText.visible = false;
            });
        }

        return tl;
    }

    public playBackgroundEffect(): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            this._effect.alpha = 1;
        })
        tl.add([this._effect.getAnimation()]);
        tl.add(() => {
            this._effect.alpha = 0;
        })
        return tl;
    }
}
